import { QueryAPI } from "@radarly/api/queries";
import {
  FocusValidity,
  GlobalBrand,
  GlobalBrandRequest,
  Query,
  QueryChange,
  QueryUses,
} from "@radarly/models/queries";
import {
  Pagination,
  QueryRepository,
  QuerySearchOptions,
} from "./QueryRepository";

interface Dependencies {
  queryAPI: QueryAPI;
}

export class APIQueryRepository implements QueryRepository {
  private queryAPI: QueryAPI;
  public constructor(dependencies: Dependencies) {
    this.queryAPI = dependencies.queryAPI;
  }
  public async getQueryDetails(project: number, focus: number) {
    return await this.queryAPI.getQueryDetails(project, focus);
  }
  public async getQueryValidity(query: string) {
    return await this.queryAPI.getQueryValidity(query);
  }
  public async searchQueries(
    search: string,
    pagination: Partial<Pagination>,
    options: Partial<QuerySearchOptions>
  ) {
    return await this.queryAPI.searchQueries(search, pagination, options);
  }
  public async getQueryUses(
    project: number,
    focus: number
  ): Promise<QueryUses> {
    return await this.queryAPI.getQueryUses(project, focus);
  }
  public async getQueriesUses(
    project: number,
    focuses: number[]
  ): Promise<QueryUses[]> {
    return await this.queryAPI.getQueriesUses(project, focuses);
  }
  public async getQueryProjectPictures(
    project: number
  ): Promise<{ pictures: { id: number; url: string }[] }> {
    return await this.queryAPI.getQueryProjectPictures(project);
  }

  public async deleteQuery(project: number, focus: number): Promise<void> {
    return await this.queryAPI.deleteQuery(project, focus);
  }
  public async deleteQueries(
    project: number,
    focuses: number[]
  ): Promise<PromiseSettledResult<number>[]> {
    return await this.queryAPI.deleteQueries(project, focuses);
  }
  public async deleteQueryProjectPicture(
    project: number,
    pictureId: number
  ): Promise<void> {
    return await this.queryAPI.deleteQueryProjectPicture(project, pictureId);
  }
  public async createQuery(query: Query, projectId: number): Promise<Query> {
    return await this.queryAPI.createQuery(projectId, query);
  }
  public async updateQuery(query: Query, projectId: number): Promise<Query> {
    return await this.queryAPI.updateQuery(projectId, query);
  }
  public async uploadQueryProjectPictures(
    projectId: number,
    files: File[]
  ): Promise<{ id: number; url: string }[]> {
    return await this.queryAPI.uploadQueryProjectPictures(projectId, files);
  }
  public async checkQueryLimit(
    query: Query,
    projectId: number
  ): Promise<{ isOverLimit: boolean }> {
    return await this.queryAPI.checkQueryLimit(projectId, query);
  }

  public async getGlobalBrands(
    request: GlobalBrandRequest,
    projectId: number
  ): Promise<{ data: GlobalBrand[]; total: number }> {
    return await this.queryAPI.getGlobalBrands(request, projectId);
  }

  public async getFocusValidity(
    query: Query,
    projectId: number
  ): Promise<{ data: FocusValidity }> {
    return await this.queryAPI.getFocusValidity(query, projectId);
  }

  public async getQueryChangeLog(
    project: number,
    focus: number,
    pagination: Pagination
  ): Promise<{ changelog: QueryChange[]; total: number }> {
    return await this.queryAPI.getQueryChangeLog(project, focus, pagination);
  }
}
